import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Wallet, Search, CheckCircle, X, Building2 } from "lucide-react";
import { toast } from "sonner";

const AdminWithdrawals = () => {
  const { getAuthHeaders } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [note, setNote] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWithdrawals();
  }, [filter]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.append("status", filter);
      const res = await axios.get(`${API}/admin/withdrawals?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      setWithdrawals(res.data.withdrawals || []);
    } catch (error) {
      toast.error("Failed to load withdrawals");
    }
    setLoading(false);
  };

  const processWithdrawal = async (action) => {
    if (!selectedWithdrawal) return;
    setProcessing(true);
    try {
      await axios.put(`${API}/admin/withdrawals/${selectedWithdrawal.id}`, {
        action,
        note,
      }, { headers: getAuthHeaders() });
      toast.success(`Withdrawal ${action}d`);
      setSelectedWithdrawal(null);
      setNote("");
      fetchWithdrawals();
    } catch (error) {
      toast.error("Failed to process withdrawal");
    }
    setProcessing(false);
  };

  return (
    <AdminLayout title="Withdrawals">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-withdrawals">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Withdrawals Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
            </div>
          ) : withdrawals.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Bank Details</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {withdrawals.map((w) => (
                    <TableRow key={w.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{w.user_name}</p>
                          <p className="text-sm text-gray-500">{w.user_mobile}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-bold text-lg">₹{w.amount.toLocaleString()}</p>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{w.bank_details?.bank_name}</p>
                          <p className="text-gray-500">{w.bank_details?.account_number}</p>
                          <p className="text-gray-400">{w.bank_details?.ifsc_code}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {new Date(w.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={
                            w.status === "approved"
                              ? "bg-green-100 text-green-700"
                              : w.status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }
                        >
                          {w.status.charAt(0).toUpperCase() + w.status.slice(1)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {w.status === "pending" && (
                          <Button
                            size="sm"
                            onClick={() => setSelectedWithdrawal(w)}
                            data-testid={`process-withdrawal-${w.id}`}
                          >
                            Process
                          </Button>
                        )}
                        {w.admin_note && (
                          <p className="text-xs text-gray-500 mt-1">
                            Note: {w.admin_note}
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No withdrawal requests</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Process Dialog */}
      <Dialog open={!!selectedWithdrawal} onOpenChange={() => setSelectedWithdrawal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Withdrawal</DialogTitle>
          </DialogHeader>
          {selectedWithdrawal && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">Amount</span>
                  <span className="font-bold text-xl">₹{selectedWithdrawal.amount.toLocaleString()}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-500">User</span>
                  <span>{selectedWithdrawal.user_name}</span>
                </div>
                <div className="border-t pt-2 mt-2">
                  <p className="text-sm font-medium mb-1 flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    Bank Details
                  </p>
                  <p className="text-sm">{selectedWithdrawal.bank_details?.bank_name}</p>
                  <p className="text-sm">{selectedWithdrawal.bank_details?.account_number}</p>
                  <p className="text-sm text-gray-500">{selectedWithdrawal.bank_details?.ifsc_code}</p>
                  <p className="text-sm">{selectedWithdrawal.bank_details?.account_holder_name}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Admin Note (Optional)</label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note..."
                  className="mt-1"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => processWithdrawal("approve")}
                  className="flex-1 bg-green-500 hover:bg-green-600"
                  disabled={processing}
                  data-testid="approve-withdrawal-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </Button>
                <Button
                  onClick={() => processWithdrawal("reject")}
                  variant="outline"
                  className="flex-1 text-red-600"
                  disabled={processing}
                  data-testid="reject-withdrawal-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminWithdrawals;
